import {
  useActiveSquad,
  useBestSquad,
} from "@/features/MyClub/ActiveSquad/useActiveSquad";
import {
  evolutionsSelector,
  fetchEvolutions,
  includedEvolutionsSelector,
  mustIncludeEvolutionsSelector,
} from "@/redux/evolutions/evolutionsSlice";
import {
  SbcSet,
  fetchSbcsSets,
  getSbcSetsSelector,
} from "@/redux/sbcs/sbcSetsSlice";
import { AppDispatch } from "@/redux/store";
import {
  type BestEvolution,
  type TopUpgrade,
  bestEvolution,
  bestSquadGain,
  nextSbc,
  recsAreStale,
  squadBuilderBudget,
  topUpgrade,
} from "@repo/shared/myClub";
import {
  fetchRecommendations,
  myClubSelector,
  recommendationsSelector,
} from "@repo/shared/redux";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export type TryNextInsights = {
  /**
   * The recommendation that lifts the squad's meta rating the most, with the
   * incoming player's own rise and the slot's rating and role today.
   */
  upgrade?: TopUpgrade;
  /** The active-squad player an evolution lifts the most. */
  evolution?: BestEvolution;
  /** The club's most meta evolution: the highest rating any club card evolves to. */
  clubEvolution?: BestEvolution;
  /**
   * The SBC to do next: Marquee Matchups while it is open, else the most
   * upvoted player SBC the club can cover.
   */
  sbc?: SbcSet;
  /**
   * The stored recommendations no longer fit the active squad (a player they
   * replace has left it): they need an update before they can be trusted.
   */
  recsStale: boolean;
  /** What the squad builder card spends: half the club's coins. */
  budget: number;
  /** The best possible squad's meta rating, and its gain on the active one. */
  bestGain?: { metaRating: number; delta: number };
};

/**
 * What the club's "Try these next" cards can say about this club in
 * particular. Each part is optional: a card without its figure falls back to
 * its general copy.
 *
 * Reads what the club already has (its stored recommendations, the SBC list)
 * and asks for one page of evolution players filtered to the active squad.
 * Nothing here spends a credit.
 */
export const useTryNextInsights = (enabled: boolean): TryNextInsights => {
  const dispatch = useDispatch<AppDispatch>();
  const recommendations = useSelector(recommendationsSelector);
  const { fetchRecommendationStatus } = useSelector(myClubSelector);
  const sbcSets = useSelector(getSbcSetsSelector).sbcsSets;
  // Read only: the club home's pitch already fetched the squad.
  const { squad, source } = useActiveSquad({ enabled: false });
  const best = useBestSquad({ name: "" });
  const [evolution, setEvolution] = useState<TryNextInsights["evolution"]>();
  const [clubEvolution, setClubEvolution] =
    useState<TryNextInsights["clubEvolution"]>();
  const { clubStats } = useSelector(myClubSelector);
  // The evolutions the player list counts by default (the ones the user can
  // start now), so the card and the list it links to agree.
  const { status: evolutionsStatus } = useSelector(evolutionsSelector);
  const includedEvolutions = useSelector(includedEvolutionsSelector);
  const mustIncludeEvolutions = useSelector(mustIncludeEvolutionsSelector);

  useEffect(() => {
    if (!enabled) return;
    if (fetchRecommendationStatus === "idle") dispatch(fetchRecommendations());
    if (sbcSets.status === "idle") dispatch(fetchSbcsSets());
    if (evolutionsStatus === "idle") dispatch(fetchEvolutions());
  }, [
    enabled,
    fetchRecommendationStatus,
    sbcSets.status,
    evolutionsStatus,
    dispatch,
  ]);

  // The starting eleven, slots 0–10: the pitch only has those.
  const starterIds = squad?.slots.map((slot) => slot.player.resourceId);
  const startersKey = starterIds?.join(",");

  useEffect(() => {
    if (!enabled) return;
    if (evolutionsStatus === "idle" || evolutionsStatus === "pending") return;
    let cancelled = false;
    const evoIds = [...includedEvolutions, ...mustIncludeEvolutions];
    // The same sorts and filters the cards' links open, so their player tops
    // that list (bench players aside).
    if (starterIds?.length)
      bestEvolution(
        { sort: "upgrade", activeSquad: true },
        evoIds,
        mustIncludeEvolutions,
        new Set(starterIds),
      )
        .then((best) => !cancelled && setEvolution(best))
        .catch(() => {});
    bestEvolution(
      { sort: "meta-rating", myClub: true },
      evoIds,
      mustIncludeEvolutions,
    )
      .then((best) => !cancelled && setClubEvolution(best))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    evolutionsStatus,
    includedEvolutions,
    mustIncludeEvolutions,
    startersKey,
  ]);

  const upgrade = useMemo(
    () => topUpgrade(recommendations, squad),
    [recommendations, squad],
  );

  const sbc = useMemo(
    () => nextSbc(sbcSets.data, clubStats.sbcFodder),
    [sbcSets.data, clubStats.sbcFodder],
  );

  const recsStale = recsAreStale(recommendations, source);

  return {
    upgrade,
    evolution,
    clubEvolution,
    sbc,
    recsStale,
    budget: squadBuilderBudget(clubStats.coins),
    bestGain: bestSquadGain(best, squad),
  };
};
